import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import RoleCard from "../components/roles/RoleCard";
import CreateRoleModal from "../components/roles/CreateRoleModal";
import UpdateRoleModal from "../components/roles/UpdateRoleModal";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { usePermissions } from "../hooks/usePermissions";

import {
  getRoles,
  deleteRole,
  resetRoleServerStatus,
} from "../redux/actions/roleActions";
import PageError from "../components/common/PageError";

const RolesAndPermissionsPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  // --- SELECTORS ---
  const {
    roles,
    loadingRoles,
    errorLoadingRoles,
    serverStatus,
    loadingRoleDelete,
    loadingRoleUpdate,
    loadingRoleCreate,
    errorCreatingRole,
    errorUpdatingRole,
    errorDeletingRole,
  } = useSelector((state) => state.role);

  // --- LOCAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // --- PERMISSIONS ---
  const canCreateRole = hasPermission("role:create");
  const canUpdateRole = hasPermission("role:update");
  const canDeleteRole = hasPermission("role:delete");

  // --- DATA FETCHING ---
  const fetchRoles = useCallback(() => {
    dispatch(getRoles());
  }, [dispatch]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = () => setIsCreateModalOpen(true);

  const handleCloseCreate = useCallback(() => {
    if (!loadingRoleCreate) setIsCreateModalOpen(false);
  });

  const handleOpenUpdate = (role) => {
    setSelectedRole(role);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingRoleUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedRole(null);
    }
  });

  const handleOpenDelete = (role) => {
    setSelectedRole(role);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingRoleDelete) {
      setIsDeleteModalOpen(false);
      setSelectedRole(null);
    }
  });

  const handleDeleteConfirm = () => {
    if (selectedRole) {
      dispatch(deleteRole(selectedRole.id));
    }
  };

  useEffect(() => {
    console.log("Roles page mounted");
  }, []);

  // Auto-close modals on success
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) {
      handleCloseCreate();
    }

    if (operation === "update" && status === 200) {
      handleCloseUpdate();
    }

    if (operation === "delete" && status === 200) {
      handleCloseDelete();
    }

    dispatch(resetRoleServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  // --- RENDER LOGIC ---
  const renderContent = () => {
    if (loadingRoles) {
      return <AppCircularProgress />;
    }

    if (errorLoadingRoles) {
      return <PageError error={errorLoadingRoles} onClick={fetchRoles} />;
    }

    if (!roles || roles.length === 0) {
      return (
        <Box textAlign="center" mt={5}>
          <Typography>No Roles Found</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {roles.map((role) => (
          <Grid item key={role.id} xs={12} sm={6} md={4} lg={3}>
            <RoleCard
              role={role}
              onEdit={canUpdateRole ? () => handleOpenUpdate(role) : undefined}
              onDelete={
                canDeleteRole ? () => handleOpenDelete(role) : undefined
              }
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Roles & Permissions"
      subtitle="Define user roles and assign specific permissions for system access."
      actions={
        canCreateRole && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Create New Role
          </AppButton>
        )
      }
    >
      {renderContent()}

      {/* MODALS */}
      {isCreateModalOpen && (
        <CreateRoleModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingRoleCreate}
          error={errorCreatingRole}
        />
      )}

      {isUpdateModalOpen && selectedRole && (
        <UpdateRoleModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          role={selectedRole}
          loading={loadingRoleUpdate}
          error={errorUpdatingRole}
        />
      )}

      {isDeleteModalOpen && selectedRole && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Role"
          message={`Are you sure you want to delete the role "${selectedRole.name}"? This action cannot be undone.`}
          loading={loadingRoleDelete}
        />
      )}
    </PageContainer>
  );
};

export default RolesAndPermissionsPage;
